// client/src/components/common/lexistarNotificationStream.ts (ST-00251)
//
// What the bell, the sidebar's Notifications badge and the notifications inbox page share, so
// they agree with each other and a tab keeps ONE live connection:
//
//   subscribeNotifications(fn)   `lexistar:notification` pushes from the /tracking socket (the
//                                server joins every session socket to its own `user:{id}` room;
//                                `lexistar:join-user` asks again after a reconnect). The socket
//                                opens with the first subscriber and closes with the last.
//   unreadStore / useLexiStarUnread()
//                                the user's unread total. The bell owns it (it loads it, counts
//                                live arrivals and its own mark-reads); the inbox page sets it
//                                from the server's answer after it marks notifications read.
//   publishMarked / subscribeMarked
//                                "these notifications were marked read" (ids, or all), so the
//                                bell's list and the page's rows show the same read state.

import { useSyncExternalStore } from 'react';
import { io, type Socket } from 'socket.io-client';

export const NOTIFICATION_EVENT = 'lexistar:notification';

/** A `lexistar:notification` push ({id, message_id, tenant_id, title, body, link, severity, created_at}). */
export interface LiveNotification {
  id: string;
  message_id?: string | null;
  tenant_id?: number;
  title?: string | null;
  body?: string | null;
  link?: string | null;
  severity?: string | null;
  created_at?: string;
  read_at?: string | null;
}

type Listener = (n: LiveNotification) => void;

const socketBaseUrl = (): string =>
  import.meta.env.REACT_APP_API_URL?.replace(/\/api\/?$/, '') || 'http://localhost:5002';

const listeners = new Set<Listener>();
let socket: Socket | null = null;

function connect(): Socket {
  const s = io(`${socketBaseUrl()}/tracking`, {
    withCredentials: true,
    transports: ['websocket', 'polling'],
  });
  s.on('connect', () => s.emit('lexistar:join-user'));
  s.on(NOTIFICATION_EVENT, (n: unknown) => {
    if (!n || typeof n !== 'object' || typeof (n as LiveNotification).id !== 'string') return;
    for (const fn of [...listeners]) fn(n as LiveNotification);
  });
  return s;
}

/** Receive every live notification of the signed-in user; returns the unsubscribe. */
export function subscribeNotifications(fn: Listener): () => void {
  listeners.add(fn);
  if (!socket) socket = connect();
  return () => {
    listeners.delete(fn);
    if (listeners.size === 0 && socket) {
      socket.disconnect();
      socket = null;
    }
  };
}

// ── Unread total ─────────────────────────────────────────────────────────────

let unread = 0;
const unreadListeners = new Set<() => void>();

export const unreadStore = {
  get: (): number => unread,
  set(n: number): void {
    const next = Math.max(0, Math.floor(Number(n)) || 0);
    if (next === unread) return;
    unread = next;
    for (const fn of [...unreadListeners]) fn();
  },
  add(delta: number): void {
    unreadStore.set(unread + delta);
  },
  subscribe(fn: () => void): () => void {
    unreadListeners.add(fn);
    return () => {
      unreadListeners.delete(fn);
    };
  },
};

/** The signed-in user's unread LexiStar notifications (0 until the bell has loaded them). */
export function useLexiStarUnread(): number {
  return useSyncExternalStore(unreadStore.subscribe, unreadStore.get, unreadStore.get);
}

// ── Marked read ──────────────────────────────────────────────────────────────

/** ids: those notifications; all: every one of the user's (the bell's Mark all read). */
export type MarkedEvent = { ids: string[] } | { all: true };
type MarkedListener = (e: MarkedEvent, source: string) => void;
const markedListeners = new Set<MarkedListener>();

/** Tell the others (not `source` itself) that notifications were marked read. */
export function publishMarked(e: MarkedEvent, source: string): void {
  for (const fn of [...markedListeners]) fn(e, source);
}

export function subscribeMarked(fn: MarkedListener): () => void {
  markedListeners.add(fn);
  return () => {
    markedListeners.delete(fn);
  };
}

/** Tests: forget the shared state between cases. */
export function resetNotificationStreamForTests(): void {
  listeners.clear();
  markedListeners.clear();
  if (socket) socket.disconnect();
  socket = null;
  unread = 0;
}
